import React from 'react'
import styled from '@emotion/styled'
import GatsbyImage from 'gatsby-image'

import useInView from '../hooks/useInView'

import siteConfig from '../../site-config'
import theme from '../styles/theme'
import { media, rem } from '../styles/tools'
import { isBrowser } from '../utils'

import Link from './Link'
import Space from './Space'
import TextUnderline from './TextUnderline'
import { WideText } from '../styles/typography'
import { Fade, Slide, Reveal } from './Animated'

const FluidRevelImg = styled(
  ({ fluid, alt = '', ratio, width, height, color, ...props }) => (
    <div {...props}>
      <GatsbyImage fluid={fluid} alt={alt} />
    </div>
  )
)(({ ratio = 2 / 3, width, height, color }) => ({
  width,
  height,
  position: 'relative',
  backgroundColor: color,

  '&::after': {
    content: '""',
    display: 'block',
    paddingTop: `${ratio * 100}%`,
  },

  '> .gatsby-image-wrapper': {
    display: 'block',
    width: '100%',
    height: '100%',
    position: 'absolute !important',
    left: 0,
    top: 0,
  },
}))

const PostTitle = styled.h3(
  {
    fontWeight: 700,
    fontSize: rem(18),
    [media.laptopMd]: { fontSize: rem(22) },
    [media.desktop]: { fontSize: rem(24) },
    [media.desktopMd]: { fontSize: rem(26) },
    textShadow: { mobile: '0 0 10px rgba(0, 0, 0, 0.42)', tabletLg: 'none' },
    zIndex: 1,
  },
  ({ maxWidth = 'auto' }) => ({ maxWidth })
)

const PostDescription = styled('p')({
  fontSize: rem(14),
  [media.laptopMd]: { fontSize: rem(16) },
  fontFamily: '"Source Serif Pro", serif',
  lineHeight: 1.8,
  opacity: 0.8,
})

const Post = ({
  as: Component = 'div',
  slug,
  title,
  description,
  direction,
  thumbnail,
  url,
  service,
  ...props
}) => {
  const [ref, inView] = useInView()

  return (
    <Component ref={ref} {...props}>
      <Link
        to={`/${siteConfig.blogPathname}/${slug}`}
        css={{
          display: 'grid',
          gridTemplateColumns: '10% 1fr',
          [media.tabletLg]: { gridTemplateColumns: 'repeat(4, 1fr)' },
          alignItems: 'center',
        }}
      >
        <div
          css={{
            position: 'relative',
            gridRow: '1',
            gridColumn: '2 / 3',
            [media.tabletLg]: {
              gridColumn: direction === 'right' ? '1 / 4' : '2 / 5',
            },
          }}
        >
          <Reveal
            inView={inView}
            direction={
              isBrowser && window.matchMedia('(min-width: 900px)').matches
                ? direction
                : 'left'
            }
          >
            <FluidRevelImg
              fluid={thumbnail.childImageSharp.fluid}
              ratio={70 / 100}
            />
          </Reveal>
        </div>
        <div
          css={{
            zIndex: 1,
            gridRow: '1',
            gridColumn: '1 / 3',
            [media.tabletLg]: {
              gridColumn: direction === 'right' ? '3 / 5' : '1 / 3',
            },
          }}
        >
          <Slide
            left
            inView={inView}
            delay={400}
            translate={80}
            css={{ [media.tabletLg]: { display: 'none' } }}
          >
            <PostTitle maxWidth="80%">
              <TextUnderline top={0.74} color="rgba(255, 255, 255, .3)">
                {title}
              </TextUnderline>
            </PostTitle>
          </Slide>
          <Slide
            direction={direction}
            delay={400}
            inView={inView}
            css={{
              [media.max.tabletLg]: { display: 'none' },
              position: 'relative',
              padding: rem(theme.space.lg),
              color: theme.colors.black,
              backgroundColor: theme.colors.white,
            }}
          >
            <Fade delay={800} inView={inView}>
              <PostTitle>
                <TextUnderline top={0.65} color="#ddd">
                  {title}
                </TextUnderline>
              </PostTitle>
              <Space y={theme.space.md} />
              <PostDescription>{description}</PostDescription>
              <Space y={theme.space.md} />
              <WideText>GO TO ARTICLE</WideText>
            </Fade>
          </Slide>
        </div>
      </Link>
    </Component>
  )
}
export default Post
